<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import { ButtonV2, Container, Modal } from '@Shelly/shelly-design-system';
	//Components
	import VirtualGroupList from './VirtualGroupList.svelte';
	import VcExtractAsDevice from './VCExtractAsDevice.svelte';
	import CreateVComponent from './CreateVComponent.svelte';
	import VcNameSettings from './VCNameSettings.svelte';
	//Helpers
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getInitVCSettings } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	import {
		createVirtualDevice,
		getVirtualComponents,
	} from 'src/helpers/virtual_helpers/virtual_components_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { VirtualComponentInterface } from 'src/types/VirtualComponents';

	export let device: Device;
	export let showModal = false;

	const type = 'group';
	const _device = device;
	const getComponentLabel = (type: string): string => {
		return `${type}:${200 + getVirtualComponents(device.id, type).length}`;
	};
	const save = async () => {
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Virtual.Add', {
			type: 'group',
			config: settings,
		});
		const resToastMessages = {
			success: $format('vc_group_create_success', {
				default: 'Virtual group created successfully.',
			}),
			danger: null,
		};
		const resData = res?.data || res?.result;
		const groupId = resData?.id;
		const errorCode = res?.error?.device_error?.code || res?.errors?.code;
		let isok = res?.isok && groupId != null;
		if (isok) {
			const deviceComponent = `group:${groupId}`;
			const saveGroupRes: SettingRequest = await virtualGroupList.save(groupId);
			isok = !!saveGroupRes?.isok;
			if (isok && extractAsDevice) {
				const createDeviceRes: SettingRequest = await createVirtualDevice(
					device,
					settings,
					deviceComponent,
				);
				isok = !!createDeviceRes?.isok;
			}
			showModal = !isok;
		} else if (errorCode === -108) {
			resToastMessages.danger = $format('vc_template_create_reached_error', {
				default: 'Reached the maximum 10 of virtual components.',
			});
		}
		onSettingsResponse({ ...res, isok }, device, resToastMessages);
		waitForResponse = false;
	};

	let component: string = getComponentLabel(type);
	let virtualGroupList: VirtualGroupList;
	let settings: VirtualComponentInterface['config'] = getInitVCSettings(type);
	let waitForResponse = false;
	let settingsError: boolean;
	let selectedItems: string[];
	let extractAsDevice = false;
	let showVCModalCreate = false;
</script>

<Modal
	title={$format('create_virtual_group', {
		default: 'Create virtual group',
	})}
	{showModal}
	on:close={() => {
		showModal = false;
	}}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<VcNameSettings bind:settings label={component} />
		<VirtualGroupList device={_device} bind:this={virtualGroupList} bind:selectedItems />
		<ButtonV2 size="m" class="mb-s" on:click={() => (showVCModalCreate = true)}
			>{$format('virtual_components_create_component', {
				default: 'Create virtual component',
			})}</ButtonV2
		>
		<VcExtractAsDevice bind:extractAsDevice bind:showModal disabled={selectedItems?.length == 0} />
	</Container>
	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			size="l"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			variant="secondary"
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => (showModal = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2
			size="l"
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon="fas fa-save"
			loading={waitForResponse}
			disabled={waitForResponse ||
				settingsError ||
				(virtualGroupList && selectedItems?.length == 0)}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={save}
		>
			{$format('button_save', { default: 'Save' })}
		</ButtonV2>
	</div>
</Modal>

{#if showVCModalCreate}
	<CreateVComponent {device} bind:showWizard={showVCModalCreate} bind:selectedItems />
{/if}
